import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { patientPropType, patientSubscriptionPropType } from 'Shared/types';
import OneTimeTextMessageModal from '../PatientMessagingEnrollmentModal/OneTimeTextMessageModal';
import PatientMessagingEnrollmentModal from '../PatientMessagingEnrollmentModal/PatientMessagingEnrollmentModal';
import PatientMessagingHistoryModal from '../PatientMessagingEnrollmentModal/PatientMessagingHistoryModal';
import PatientMessagingUnenrollmentModal from '../PatientMessagingEnrollmentModal/PatientMessagingUnenrollmentModal';
import SendOneTimeTextModal from '../PatientMessagingEnrollmentModal/SendOneTimeTextModal';
import MessagingStatus from './MessagingStatus';
import useFetchAllBranchSettingsForTypeQuery from 'App/hooks/useFetchAllBranchSettingsForTypeQuery';
import { useUserContext } from 'App/contexts/UserContext';

const getSettingValueForBranch = (settings, branchId) => {
    if (!Array.isArray(settings)) {
        return null;
    }

    const branchSetting = settings.find((setting) => setting.branchId === branchId);

    if (branchSetting) {
        return branchSetting.keyValue;
    }

    const defaultSetting = settings.find((setting) => setting.branchId === null);

    return defaultSetting?.keyValue ?? null;
};

const PatientEngagement = ({
    patientDemographicsData: patient,
    patientMessagingSubscription: subscription,
    refetchMessagingSubscriptions: refetch,
    subscribedComponent,
}) => {
    const user = useUserContext();
    const activeBranchId = user?.active_branch?.ID;

    const { isLoading: messagingBranchNameLoading, data: messagingBranchNameSettings } =
        useFetchAllBranchSettingsForTypeQuery('messagingBranchName');
    const { isLoading: messagingPhoneLoading, data: messagingPhoneSettings } =
        useFetchAllBranchSettingsForTypeQuery('messagingPhone');

    const messagingBranchName = getSettingValueForBranch(messagingBranchNameSettings, activeBranchId);
    const messagingPhone = getSettingValueForBranch(messagingPhoneSettings, activeBranchId);

    const hasMessagingBranchNameSettings = Boolean(messagingBranchName);
    const hasMessagingPhoneSettings = Boolean(messagingPhone);
    const isBranchConfiguredForPatientMessaging = hasMessagingBranchNameSettings && hasMessagingPhoneSettings;
    const isMessagingBranchSettingsLoading = messagingBranchNameLoading || messagingPhoneLoading;
    const canEnrollPatientMessaging = !isMessagingBranchSettingsLoading && isBranchConfiguredForPatientMessaging;

    const [showPatientMessagingModal, setShowPatientMessagingModal] = useState(false);
    const [showPatientMessagingUnenrollmentModal, setShowPatientMessagingUnenrollmentModal] = useState(false);

    const [showMessagingHistoryModal, setShowMessagingHistoryModal] = useState(false);
    const [showOneTimeMessage, setShowOneTimeMessage] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const { mobilePhone, id: patientId, divisionId } = patient;

    return (
        <div data-testid="patient-engagement">
            <div>
                {isMessagingBranchSettingsLoading ? (
                    <div style={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                        <i className="fa fa-spinner fa-spin text-primary" data-testid="patient-engagement-loading" />
                    </div>
                ) : (
                    <MessagingStatus
                        subscription={subscription}
                        subscribedComponent={subscribedComponent}
                        canEnrollPatientMessaging={canEnrollPatientMessaging}
                        showOneTimeModal={() => setShowOneTimeMessage(true)}
                        showMessagingModal={() => setShowPatientMessagingModal(true)}
                        showHistoryModal={() => setShowMessagingHistoryModal(true)}
                        showUnenrollmentModal={() => setShowPatientMessagingUnenrollmentModal(true)}
                    />
                )}
            </div>

            {showPatientMessagingUnenrollmentModal && (
                <PatientMessagingUnenrollmentModal
                    mobilePhone={mobilePhone}
                    subscriptionValue={subscription}
                    patientId={patientId}
                    show={showPatientMessagingUnenrollmentModal}
                    onHide={(wasSuccess = false) => {
                        setShowPatientMessagingUnenrollmentModal(false);
                        if (wasSuccess) {
                            refetch();
                        }
                    }}
                />
            )}
            {showPatientMessagingModal && (
                <PatientMessagingEnrollmentModal
                    mobilePhone={mobilePhone}
                    subscriptionValue={subscription}
                    patientId={patientId}
                    show={showPatientMessagingModal}
                    onHide={(wasSuccess = false) => {
                        setShowPatientMessagingModal(false);
                        if (wasSuccess) {
                            refetch();
                        }
                    }}
                />
            )}
            {showMessagingHistoryModal && (
                <PatientMessagingHistoryModal
                    show={showMessagingHistoryModal}
                    onHide={() => setShowMessagingHistoryModal(false)}
                    patientId={patientId}
                    divisionId={divisionId}
                />
            )}
            {showOneTimeMessage && (
                <OneTimeTextMessageModal
                    onClose={(template) => {
                        setShowOneTimeMessage(false);
                        if (template) {
                            setSelectedTemplate(template);
                        }
                    }}
                />
            )}
            {patientId && selectedTemplate && (
                <SendOneTimeTextModal
                    patientId={patientId}
                    onClose={() => {
                        setSelectedTemplate(null);
                    }}
                    selectedTemplate={selectedTemplate}
                />
            )}
        </div>
    );
};

PatientEngagement.propTypes = {
    patientDemographicsData: patientPropType.isRequired,
    patientMessagingSubscription: patientSubscriptionPropType,
    refetchMessagingSubscriptions: PropTypes.func.isRequired,
    subscribedComponent: PropTypes.elementType,
};

PatientEngagement.defaultProps = {
    patientMessagingSubscription: null,
    subscribedComponent: undefined,
};

export default PatientEngagement;
